/* T&F Cart drawer + page interactions
 * - Slide drawer on right; opens on cart icon click and after add-to-cart
 * - Uses Shopify Section Rendering API to refresh drawer & cart icon
 * - Cart page: quantity select + remove update via /cart/change.js
 */
(function () {
  'use strict';

  const SECTION_DRAWER = 'tandf-cart-drawer';
  const SECTION_ICON = 'tandf-cart-icon-fragment';
  const SECTION_PAGE = 'tandf-main-cart';

  /* ----------------- Drawer ----------------- */
  class TandfCartDrawer extends HTMLElement {
    connectedCallback() {
      this.captureRefs();
      this.bindStaticHandlers();
      this.bindItemHandlers();

      document.addEventListener('cart:item-added', () => this.refreshAndOpen('added'));

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-tf-cart-toggle]');
        if (trigger) {
          e.preventDefault();
          this.refreshAndOpen('default');
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    }

    captureRefs() {
      this.root = this.querySelector('.tf-drawer');
      this.overlay = this.querySelector('.tf-drawer__overlay');
      this.body = this.querySelector('.tf-drawer__body');
      this.titleEl = this.querySelector('.tf-drawer__title');
      this.defaultTitle = this.dataset.defaultTitle || 'YOUR SHOPPING BAG';
      this.addedTitle = this.dataset.addedTitle || 'ADDED TO YOUR SHOPPING BAG';
    }

    bindStaticHandlers() {
      this.querySelectorAll('[data-tf-drawer-close]').forEach((b) =>
        b.addEventListener('click', () => this.close())
      );
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    }

    bindItemHandlers() {
      this.querySelectorAll('[data-tf-remove]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const key = btn.dataset.tfRemove;
          if (key) this.removeItem(key);
        });
      });

      this.querySelectorAll('[data-tf-qty-decrement]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const key = btn.dataset.key;
          const input = this.querySelector(`[data-tf-qty][data-key="${key}"]`);
          if (!input) return;
          const next = Math.max(0, parseInt(input.value, 10) - 1);
          input.value = next;
          this.updateQty(key, next);
        });
      });

      this.querySelectorAll('[data-tf-qty-increment]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const key = btn.dataset.key;
          const input = this.querySelector(`[data-tf-qty][data-key="${key}"]`);
          if (!input) return;
          const max = parseInt(input.getAttribute('max'), 10) || 99;
          const next = Math.min(max, parseInt(input.value, 10) + 1);
          input.value = next;
          this.updateQty(key, next);
        });
      });

      this.querySelectorAll('[data-tf-qty]').forEach((input) => {
        input.addEventListener('change', () => {
          const qty = Math.max(0, parseInt(input.value, 10));
          if (input.dataset.key && Number.isFinite(qty)) this.updateQty(input.dataset.key, qty);
        });
      });
    }

    async updateQty(key, qty) {
      const wasOpen = this.isOpen();
      const row = this.querySelector(`.tf-cart-item[data-line-key="${key}"]`);
      const summary = document.querySelector('.tf-summary');
      if (row) row.setAttribute('aria-busy', 'true');
      if (summary) summary.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(rootPath('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
        if (!res.ok) throw new Error(await res.text());
        await refreshAll({ drawer: this, page: document.querySelector('tandf-main-cart') });
      } catch (err) {
        if (row) row.removeAttribute('aria-busy');
        if (summary) summary.removeAttribute('aria-busy');
        console.error('[T&F cart] qty update failed', err);
        return;
      }
      if (wasOpen) this.open('default');
      flashUpdatedPrice(this, key);
      if (qty === 0) this.showToast('Item removed from your bag');
    }

    isOpen() {
      return this.root && this.root.getAttribute('data-open') === 'true';
    }

    open(mode) {
      if (!this.root) return;
      if (this.titleEl) {
        this.titleEl.textContent = mode === 'added' ? this.addedTitle : this.defaultTitle;
      }
      this.root.setAttribute('data-open', 'true');
      document.documentElement.style.overflow = 'hidden';
    }

    close() {
      if (!this.root) return;
      this.root.setAttribute('data-open', 'false');
      document.documentElement.style.overflow = '';
    }

    async refreshAndOpen(mode) {
      try {
        await refreshAll({ drawer: this, page: true });
      } catch (err) {
        console.error('[T&F cart] refresh failed', err);
      }
      this.open(mode);
    }

    async removeItem(key) {
      const wasOpen = this.isOpen();
      try {
        const res = await fetch(rootPath('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!res.ok) throw new Error(await res.text());
        await refreshAll({ drawer: this, page: true });
      } catch (err) {
        console.error('[T&F cart] remove failed', err);
        return;
      }
      // refreshAll wipes innerHTML, which resets data-open on .tf-drawer.
      // Re-open so the user keeps their place and surface a small toast.
      if (wasOpen) this.open('default');
      this.showToast('Item removed from your bag');
    }

    showToast(message) {
      if (!this.toastEl) {
        this.toastEl = document.createElement('div');
        this.toastEl.className = 'tf-drawer__toast';
        this.toastEl.setAttribute('role', 'status');
        this.toastEl.setAttribute('aria-live', 'polite');
        // Append outside .tf-drawer so refresh innerHTML replacement won't wipe it.
        this.appendChild(this.toastEl);
      }
      this.toastEl.textContent = message;
      this.toastEl.classList.remove('tf-drawer__toast--visible');
      requestAnimationFrame(() => {
        this.toastEl?.classList.add('tf-drawer__toast--visible');
      });
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastEl?.classList.remove('tf-drawer__toast--visible');
      }, 2200);
    }
  }

  /* ----------------- Cart Page items + summary host ----------------- */
  class TandfMainCart extends HTMLElement {
    connectedCallback() {
      this.bindHandlers();
    }

    bindHandlers() {
      this.querySelectorAll('[data-tf-qty]').forEach((input) => {
        input.addEventListener('change', () => {
          const key = input.dataset.key;
          const qty = parseInt(input.value, 10);
          if (key && Number.isFinite(qty)) this.updateQty(key, Math.max(0, qty));
        });
      });

      this.querySelectorAll('[data-tf-qty-decrement]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const key = btn.dataset.key;
          const input = this.querySelector(`[data-tf-qty][data-key="${key}"]`);
          if (!input) return;
          const next = Math.max(0, parseInt(input.value, 10) - 1);
          input.value = next;
          this.updateQty(key, next);
        });
      });

      this.querySelectorAll('[data-tf-qty-increment]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const key = btn.dataset.key;
          const input = this.querySelector(`[data-tf-qty][data-key="${key}"]`);
          if (!input) return;
          const max = parseInt(input.getAttribute('max'), 10) || 99;
          const next = Math.min(max, parseInt(input.value, 10) + 1);
          input.value = next;
          this.updateQty(key, next);
        });
      });

      this.querySelectorAll('[data-tf-remove]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const key = btn.dataset.tfRemove;
          if (key) this.updateQty(key, 0);
        });
      });
    }

    async updateQty(key, qty) {
      const row = this.querySelector(`.tf-cart-item[data-line-key="${key}"]`);
      const summary = this.querySelector('.tf-summary');
      if (row) row.setAttribute('aria-busy', 'true');
      if (summary) summary.setAttribute('aria-busy', 'true');
      this.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(rootPath('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
        if (!res.ok) throw new Error(await res.text());
        await refreshAll({
          page: true,
          drawer: document.querySelector('tandf-cart-drawer')
        });
        flashUpdatedPrice(this, key);
      } catch (err) {
        if (row) row.removeAttribute('aria-busy');
        if (summary) summary.removeAttribute('aria-busy');
        console.error('[T&F cart] qty update failed', err);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }

  /* Briefly highlight the price element for the updated line so the user
     sees that something changed after the silent network refresh. */
  function flashUpdatedPrice(scope, key) {
    const price = scope.querySelector(`[data-tf-price][data-line-key="${key}"]`);
    if (!price) return;
    price.classList.remove('tf-cart-item__price--updated');
    requestAnimationFrame(() => {
      price.classList.add('tf-cart-item__price--updated');
      setTimeout(() => price.classList.remove('tf-cart-item__price--updated'), 800);
    });
  }

  /* ----------------- Helpers ----------------- */
  function rootPath(p) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/' + p;
  }

  async function refreshAll({ drawer, page }) {
    const sections = [SECTION_ICON];
    if (drawer) sections.push(SECTION_DRAWER);
    if (page && document.querySelector('tandf-main-cart')) sections.push(SECTION_PAGE);

    const root = rootPath('');
    const url = `${root}?sections=${sections.join(',')}&_cb=${Date.now()}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error(`refresh ${res.status}`);
    const data = await res.json();

    if (drawer && data[SECTION_DRAWER]) replaceDrawerHTML(drawer, data[SECTION_DRAWER]);
    if (data[SECTION_ICON]) replaceIcon(data[SECTION_ICON]);
    if (page && data[SECTION_PAGE]) replacePageHTML(data[SECTION_PAGE]);
  }

  function parseHTML(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content;
  }

  function replaceDrawerHTML(drawer, html) {
    const fresh = parseHTML(html).querySelector('tandf-cart-drawer');
    if (!fresh) return;
    drawer.innerHTML = fresh.innerHTML;
    drawer.captureRefs();
    drawer.bindStaticHandlers();
    drawer.bindItemHandlers();
  }

  function replaceIcon(html) {
    const fresh = parseHTML(html).querySelector('[data-tf-cart-count]');
    const target = document.querySelector('[data-tf-cart-count]');
    if (fresh && target) target.textContent = fresh.textContent;
  }

  function replacePageHTML(html) {
    const fresh = parseHTML(html).querySelector('tandf-main-cart');
    const host = document.querySelector('tandf-main-cart');
    if (!fresh || !host) return;
    host.innerHTML = fresh.innerHTML;
    if (typeof host.bindHandlers === 'function') host.bindHandlers();
  }

  if (!customElements.get('tandf-cart-drawer')) {
    customElements.define('tandf-cart-drawer', TandfCartDrawer);
  }
  if (!customElements.get('tandf-main-cart')) {
    customElements.define('tandf-main-cart', TandfMainCart);
  }
})();
